import "./add-to-cart/add-to-cart";
import "./accordion/accordion";
import "./conditional-details/conditional-details";
import "./video/video";
import "./tab/tab";
import "./price-snippet/price-snippet";
import "./klaviyo/klaviyo-form";
import "./dynamic-breadcrumbs/dynamic-breadcrumbs";
import "./markup-modal/markup-modal";
import "./back-in-stock/back-in-stock-button";
import "./back-in-stock/back-in-stock-form";
import "./validate-input/validate-input";

import Swiper from "swiper";
import {
  Autoplay,
  EffectFade,
  Navigation,
  Pagination,
  Mousewheel,
  Thumbs,
} from "swiper/modules";
import { register } from "swiper/element/bundle";

register();

// Load Swiper once for re-use by any other components
window.loadSwiperFulfillPromise({
  Swiper,
  Autoplay,
  EffectFade,
  Navigation,
  Pagination,
  Mousewheel,
  Thumbs,
});

/**
 * Sets Document Hight Variable for mobile devices
 */
const documentHeight = () => {
  const doc = document.documentElement;
  doc.style.setProperty("--window-height", `${window.screen.height}px`);
};
window.addEventListener("resize", documentHeight);
documentHeight();

/**
 * Scrapes a section element for data needing to be handled in section JS
 *
 * @param {DOMNode} container
 * @returns parsed JSON
 */
window.scrapeSectionData = (container) => {
  const dataEl = container.querySelector("[data-section-data]");

  if (!dataEl) return {};

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    console.warn(
      `Error parsing data for ${container.dataset.sectionType} ${container.dataset.sectionId}`,
    );
    return {};
  }
};

/**
 * Fetchs page data from alternate templates
 *
 * @param {String} path
 * @param {String} templateSuffix
 * @returns parsed JSON
 */
window.fetchPageData = async ({ path, options = {} }) => {
  if (!path) return {};

  const { templateSuffix, sectionId, variantId } = options;
  const queryArray = [];

  if (templateSuffix) queryArray.push(`view=${templateSuffix}`);
  if (sectionId) queryArray.push(`section_id=${sectionId}`);
  if (variantId) queryArray.push(`variant=${variantId}`);

  const url = `${path}?${queryArray.join("&")}`;
  let html = {};

  await fetch(url)
    .then((response) => response.text())
    .then((response) => {
      const elId = sectionId ? `shopify-section-${sectionId}` : "MainContent";
      html = new DOMParser().parseFromString(response, "text/html");
      html = html.getElementById(elId).innerHTML;
    })
    .catch(() => {
      console.warn(`Error fetching data from ${url}`);
    });

  return html;
};

window.getSectionInnerHTML = (html, selector) => {
  return new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(selector)?.innerHTML;
};

/**
 * Base-64 encodes an ID
 *
 * @param {String} id resource ID
 * @param {String} type resource type
 * @returns encoded ID
 */
window.encodeId = (id, type) => {
  return window.btoa(`gid://shopify/${type}/${id}`);
};

window.idToGid = (id, type) => {
  return `gid://shopify/${type}/${id}`;
};

window.gidToId = (id) => {
  return id.replace(/^\D*/, "");
};

/**
 * Decode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {String} id resource ID
 * @param {String} type resource type
 * @returns decoded ID
 */
window.decodeId = (id, type) => {
  return decodeURIComponent(escape(window.atob(id))).split(
    `gid://shopify/${type}/`,
  )[1];
};

/**
 * Formats a given integer to a standard fraction.
 *
 * @param {Integer} num A price, with cents
 * @param {Integer} fraction
 */
window.formatMoney = (num, maximumFractionDigits = 0, currency = "USD") => {
  return new Intl.NumberFormat("en-EN", {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(num);
};

/**
 * Handleize string
 *
 * @param {string} `products array Object`
 * @returns handleized string object
 *
 */
window.handleize = (string) => {
  string = string.toLowerCase();

  const toReplace = ['"', "'", "\\", "(", ")", "[", "]"];

  // For the old browsers
  for (let i = 0; i < toReplace.length; ++i) {
    string = string.replace(toReplace[i], "");
  }

  string = string.replace(/\W+/g, "-");

  if (string.charAt(string.length - 1) == "-") {
    string = string.replace(/-+\z/, "");
  }

  if (string.charAt(0) == "-") {
    string = string.replace(/\A-+/, "");
  }

  return string;
};

// Resize Shopify Image URL
window.getSizedImageUrl = (src, size) => {
  // remove any current image size then add the new image size
  if (typeof src !== "undefined") {
    return src.replace(/\.jpg|\.png|\.webp|\.gif|\.jpeg/g, (match) => {
      return `_${size}${match}`;
    });
  } else {
    return "";
  }
};

/**
 * Change an item in the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.changeCart = (change, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  fetch("/cart/change.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...change, sections: "mini-cart" }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422) {
        if (options?.onError) {
          options.onError(response);
        }

        return;
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          data: options.data,
        },
      });
      document.dispatchEvent(updateCart);

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch(console.error);
};

/**
 * Remove an item from the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.updateCart = (updates, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...updates, sections: "mini-cart" }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422 && response.message === "Cart Error") {
        if (options?.onError) {
          options.onError(response);
        }

        return;
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          data: options.data,
        },
      });
      document.dispatchEvent(updateCart);

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch(console.error);
};

/**
 * Add an item to cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.addToCart = (items, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }
  if (!Array.isArray(items)) {
    items = [items];
  }
  const data = {
    items: [...items],
    sections: "mini-cart",
  };

  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      if (!response.ok) {
        if (options?.onError) {
          options.onError(response);
        }

        throw new Error("Error adding to cart");
      }

      return response.json();
    })
    .then((response) => {
      const sections = response.sections;

      fetch("/cart.js", {
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((response) => {
          const updateCart = new CustomEvent("updateCart", {
            detail: {
              cart: { ...response, sections },
              data: options.data,
            },
          });
          document.dispatchEvent(updateCart);
        });

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch(console.error);
};

window.debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

window.onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
};
window.removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

window.trapFocus = (container, elementToFocus = container) => {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
};

const lockers = new Set();

window.lockScroll = (locker) => {
  lockers.add(locker);
  document.documentElement.style.overflow = "hidden";
};

window.unlockScroll = (locker) => {
  lockers.delete(locker);

  if (!lockers.size) {
    document.documentElement.style.overflow = null;
  }
};

// Below header dimmer (for mega menus, search, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".below-header-dimmer");

  window.openDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

// Full screen dimmer (for drawers, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".full-screen-dimmer");

  window.openFullDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeFullDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();
